<script>
import { getBusinessCards, toggleBusinessCard, regenerateCardToken, deleteBusinessCard } from '@/utils/business-card';
import BusinessCardStatsModal from './business-card-stats-modal.vue';
import BusinessCardQrModal from './business-card-qr-modal.vue';

export default {
components: { BusinessCardStatsModal, BusinessCardQrModal },
  inject: ['setLoading'],
  data() {
    return {
      loading: true,
      title: 'CRM',
      text: 'CRM',
      data: [],
      tableColumns: [
        { key: 'user', label: 'User' },
        { key: 'designation', label: 'Designation' },
        { key: 'company', label: 'Company' },
        { key: 'phone', label: 'Phone' },
        { key: 'theme', label: 'Theme' },
        { key: 'view_count', label: 'Views' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: [],
      },
      failed_images: {},
    };
  },

  computed: {
    text1() {
      return `Business Cards (${this.$numFormat(this.pagination.total)})`;
    },
  },

  mounted() {
    this.cards();
  },

  methods: {
    async cards(page = 1) {
      this.loading = true;
      try {
        const response = await getBusinessCards({
          page,
          per_page: this.pagination.per_page,
        });
        const res = response.data.data;
        this.data = res.data;
        this.failed_images = {};
        this.pagination = {
          current_page: res.current_page,
          per_page: res.per_page,
          last_page: res.last_page,
          total: res.total,
          links: res.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    publicUrl(token) {
      return `${window.location.origin}/card/${token}`;
    },

    async copyLink(token) {
      try {
        await navigator.clipboard.writeText(this.publicUrl(token));
        this.$swalToast('Link copied');
      } catch (e) {
        this.$swalToast('Copy failed', 'error');
      }
    },

    openCard(token) {
      window.open(this.publicUrl(token), '_blank');
    },

    async toggleActive(row) {
      this.setLoading(true);
      try {
        const response = await toggleBusinessCard(row.id);
        this.$swalToast(response.data.message);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
        // The toggle response has no user object, so refetch the list
        this.cards(this.pagination.current_page);
      }
    },

    async regenerate(row) {
      const result = await this.$swal.fire({
        title: 'Regenerate link?',
        text: 'The old QR code and link will stop working.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, regenerate',
      });
      if (!result.isConfirmed) return;

      this.setLoading(true);
      try {
        const response = await regenerateCardToken(row.id);
        this.$swalToast(response.data.message);
        this.cards(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async remove(row) {
      const result = await this.$swal.fire({
        title: 'Delete this card?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
      });
      if (!result.isConfirmed) return;

      this.setLoading(true);
      try {
        const response = await deleteBusinessCard(row.id);
        this.$swalToast(response.data.message);
        // If the last row on this page was deleted, go back one page
        const page = this.data.length === 1 && this.pagination.current_page > 1
          ? this.pagination.current_page - 1
          : this.pagination.current_page;
        this.cards(page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    avatarColor(name) {
      return `hsl(${((name || 'A').charCodeAt(0) * 37) % 360}, 60%, 55%)`;
    },
  },
};
</script>

<style scoped>
.l-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  object-fit: cover;
}
.l-meta {
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 5px;
}
.l-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}
.l-theme-badge {
  font-size: 10.5px;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2px 10px;
  text-transform: capitalize;
}
</style>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">

          <vue-select
            v-model="pagination.per_page"
            :options="[
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '75', value: 75 },
              { label: '100', value: 100 },
            ]"
            label="label"
            :reduce="p => p.value"
            placeholder="Per Page"
            :clearable="false"
            @update:modelValue="cards()"
            style="min-width: 100px;"
          ></vue-select>

          <!-- Add Card -->
          <a href="javascript:void(0);" data-bs-toggle="modal" data-bs-target="#business_card_modal"
            class="btn btn-primary d-flex align-items-center" @click="$refs.cardModal.add()">
            <i class="ti ti-circle-plus me-2"></i>Add Card
          </a>
        </div>
      </div>

      <!-- Main Card -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
        <div class="card-header bg-white border-bottom py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Business Cards
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">
              ({{ $numFormat(pagination.total) }})
            </span>
          </h5>
        </div>

        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">
          <app-table :columns="tableColumns" :rows="data" :pagination="pagination" :loading="loading"
            :skeleton-rows="10" empty-text="No business cards found." empty-icon="ti ti-id-badge-2"
            @page-change="cards">

            <!-- User -->
            <template #user="{ row }">
              <div class="d-flex align-items-center gap-2">
                <img v-if="row.user?.image_url && !failed_images[row.id]"
                :src="row.user.image_url"
                class="l-avatar"
                @error="failed_images[row.id] = true" />
                <div v-else class="l-avatar" :style="{ background: avatarColor(row.user?.name) }">
                  {{ (row.user?.name || '').charAt(0).toUpperCase() }}
                </div>
                <div>
                  <div class="l-name">{{ row.user?.name }}</div>
                  <div class="l-meta" v-if="row.user?.email">{{ row.user.email }}</div>
                </div>
              </div>
            </template>

            <!-- Designation -->
            <template #designation="{ row }">
              <span style="font-size:12px;">{{ row.designation || '-' }}</span>
            </template>

            <!-- Company -->
            <template #company="{ row }">
              <span style="font-size:12px;">{{ row.company || '-' }}</span>
            </template>

            <!-- Phone -->
            <template #phone="{ row }">
              <span class="l-meta">
                <i class="ti ti-phone" style="color:#6366f1;"></i>{{ row.phone || '-' }}
              </span>
            </template>

            <!-- Theme -->
            <template #theme="{ row }">
              <span class="l-theme-badge">{{ row.theme }}</span>
            </template>

            <!-- Views -->
            <template #view_count="{ row }">
              <span class="l-meta"><i class="ti ti-eye"></i>{{ row.view_count }}</span>
            </template>

            <!-- Status -->
            <template #is_active="{ row }">
              <div class="form-check form-switch mb-0">
                <input class="form-check-input" type="checkbox" role="switch"
                  :checked="row.is_active" @change="toggleActive(row)">
              </div>
            </template>

            <!-- Actions -->
            <template #actions="{ row }">
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown"
                  aria-expanded="false">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" data-bs-toggle="modal"
                      data-bs-target="#business_card_modal" @click="$refs.cardModal.edit(row)">
                      <i class="ti ti-edit me-1"></i>Edit
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openCard(row.public_token)">
                      <i class="ti ti-external-link me-1"></i>Open Card
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="copyLink(row.public_token)">
                      <i class="ti ti-copy me-1"></i>Copy Link
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" data-bs-toggle="modal"
                        data-bs-target="#qr_modal" @click="$refs.qrModal.open(row)">
                        <i class="ti ti-qrcode me-1"></i>QR Code
                    </a>
                    </li>
                    <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" data-bs-toggle="modal"
                        data-bs-target="#stats_modal" @click="$refs.statsModal.open(row)">
                        <i class="ti ti-chart-line me-1"></i>Stats
                    </a>
                    </li>
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="regenerate(row)">
                      <i class="ti ti-refresh me-1"></i>Regenerate Link
                    </a>
                  </li>
                  <li><hr class="dropdown-divider"></li>
                  <li>
                    <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="remove(row)">
                      <i class="ti ti-trash me-1"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </template>

          </app-table>
        </div>
      </div>
    </div>

    <app-footer></app-footer>
  </div>

  <business-card-modal ref="cardModal" @updated="cards"></business-card-modal>
  <business-card-stats-modal ref="statsModal"></business-card-stats-modal>
  <business-card-qr-modal ref="qrModal"></business-card-qr-modal>
</template>